import React from 'react';

import { AdornedInput, Adornment, ButtonGroup, FormField, Heading, Label, ToggleButton } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../../models';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../../../constants/stringConstants';
import YesAndNoExpansion from '../../../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  utilityOwnRentalPropertyYesField: string;
  utilityOwnRentalPropertyNoField: string;
  annualCostsField: string;
  knowAnnualCostsSimilarRentalUnitField: string;
  setSectionValues: (keyValuePairs: object) => void;
  utilityName: string;
};

export default function UtilitiesOwnRentalPropertyAnnualCosts({
  section,
  setSectionValues,
  utilityName,
  utilityOwnRentalPropertyYesField,
  utilityOwnRentalPropertyNoField,
  annualCostsField,
  knowAnnualCostsSimilarRentalUnitField,
}: Props): JSX.Element {
  function onNoOwnRentalProperty(): void {
    setSectionValues({
      [utilityOwnRentalPropertyYesField]: false,
      [utilityOwnRentalPropertyNoField]: true,
    });
  }

  function onYesOwnRentalProperty(): void {
    setSectionValues({
      [utilityOwnRentalPropertyYesField]: true,
      [utilityOwnRentalPropertyNoField]: false,
    });
  }

  function onNoKnowAnnualCostsSimilarRentalUnitField(): void {
    setSectionValues({
      [knowAnnualCostsSimilarRentalUnitField]: false,
    });
  }

  function onYesKnowAnnualCostsSimilarRentalUnitField(): void {
    setSectionValues({
      [knowAnnualCostsSimilarRentalUnitField]: true,
    });
  }

  function onAnnualCostsChange(event): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [annualCostsField]: value,
    });
  }

  const yesFieldValue = section.getFieldValue(utilityOwnRentalPropertyYesField);
  const noFieldValue = section.getFieldValue(utilityOwnRentalPropertyNoField);

  return (
    <>
      <Heading level={6} marginBottom="sm">
        {utilityName}
      </Heading>
      <FormField
        label={
          <Label>{`Did you own the rental property during the previous 12 months and pay the ${utilityName.toLowerCase()} costs to the utility provider on behalf of the tenant?`}</Label>
        }
        control={
          <ButtonGroup fluid marginBottom="sm" conjoined aria-label="tenant parking">
            <ToggleButton onClick={onYesOwnRentalProperty} pressed={yesFieldValue}>
              Yes
            </ToggleButton>
            <ToggleButton onClick={onNoOwnRentalProperty} pressed={noFieldValue}>
              No
            </ToggleButton>
          </ButtonGroup>
        }
      />
      {yesFieldValue && (
        <FormField
          label={
            <Label>
              What are the annual costs of service from the utility provider that provided the primary service during
              the previous 12 months?
            </Label>
          }
          control={
            <AdornedInput
              input={
                <CurrencyInput
                  value={section.getFieldValue(annualCostsField)}
                  placeholder={CURRENCY_INPUT_PLACEHOLDER}
                  onChange={onAnnualCostsChange}
                />
              }
              leftAdornment={<Adornment>$</Adornment>}
            />
          }
        />
      )}
      {noFieldValue && (
        <FormField
          label={<Label>Do you know the annual costs of service for a similar rental unit?</Label>}
          control={
            <YesAndNoExpansion
              yesActive={section.getFieldValue(knowAnnualCostsSimilarRentalUnitField)}
              yesHandler={onYesKnowAnnualCostsSimilarRentalUnitField}
              noHandler={onNoKnowAnnualCostsSimilarRentalUnitField}
            >
              <FormField
                label={<Label>What are the annual costs?</Label>}
                control={
                  <AdornedInput
                    input={
                      <CurrencyInput
                        value={section.getFieldValue(annualCostsField)}
                        placeholder={CURRENCY_INPUT_PLACEHOLDER}
                        onChange={onAnnualCostsChange}
                      />
                    }
                    leftAdornment={<Adornment>$</Adornment>}
                  />
                }
              />
            </YesAndNoExpansion>
          }
        />
      )}
    </>
  );
}
